import { useScenarioQuery } from './useScenarioQuery'
import { useAssetsQuery } from './useAssetsQuery'
import { useForecastsQuery } from './useForecastsQuery'
import { useCashflowEventsQuery } from './useCashflowEventsQuery'
import { useLiabilitiesQuery } from './useLiabilitiesQuery'
import { useNotesQuery } from './useNotesQuery'
import { useRiskProfileQuery } from './useRiskProfileQuery'
import { useBudgetQuery } from './useBudgetQuery'
import { useFnaQuery } from './useFnaQuery'
import { useAssetAllocationQuery } from './useAssetAllocationQuery'
import { useRiskProductsQuery } from './useRiskProductsQuery'

import { scenarioQueryKeys } from '../queryKeys'

/**
 * Wrapper over react-query for managing scenario planning state with the
 * server.
 * It also serves as a wrapper over the api of its associations which
 * can be used on its own.
 */
export function useScenarioAndAssociationsQuery({
  scenarioID, defaultValues, additionalInvalidationKeys = []
}) {
  /* Scenario query */
  const scenario = useScenarioQuery({
    scenarioID,
    initialData: defaultValues.scenario,
    queryKey: [scenarioQueryKeys(scenarioID).scenario],
  })

  /* Association queries */
  const assets = useAssetsQuery({
    scenarioID,
    initialData: defaultValues.assets,
    queryKey: [scenarioQueryKeys(scenarioID).assets],
    additionalInvalidationKeys
  })
  const forecast = useForecastsQuery({
    scenarioID,
    initialData: defaultValues.forecast,
    queryKey: [scenarioQueryKeys(scenarioID).forecasts],
  })
  const cashflowEvents = useCashflowEventsQuery({
    scenarioID,
    initialData: defaultValues.cashflowEvents,
    queryKey: [scenarioQueryKeys(scenarioID).cashflowEvents],
    additionalInvalidationKeys
  })
  const liabilities = useLiabilitiesQuery({
    scenarioID,
    initialData: defaultValues.liabilities,
    queryKey: [scenarioQueryKeys(scenarioID).liabilities]
  })
  const notes = useNotesQuery({
    scenarioID,
    initialData: defaultValues.notes,
    queryKey: [scenarioQueryKeys(scenarioID).notes]
  })
  const riskProfile = useRiskProfileQuery({
    scenarioID,
    initialData: defaultValues.riskProfile,
    queryKey: [scenarioQueryKeys(scenarioID).riskProfile],
    additionalInvalidationKeys
  })
  const budget = useBudgetQuery({
    scenarioID,
    initialData: defaultValues.budget,
    queryKey: [scenarioQueryKeys(scenarioID).budget],
    additionalInvalidationKeys
  })
  const fna = useFnaQuery({
    scenarioID,
    initialData: defaultValues.fna,
    queryKey: [scenarioQueryKeys(scenarioID).fna]
  })
  const assetAllocation = useAssetAllocationQuery({
    scenarioID,
    initialData: defaultValues.assetAllocation,
    queryKey: [scenarioQueryKeys(scenarioID).assetAllocation]
  })
  const riskProducts = useRiskProductsQuery({
    scenarioID,
    initialData: defaultValues.riskProducts,
    queryKey: [scenarioQueryKeys(scenarioID).riskProducts]
  })

  const isFetching = scenario.isFetching ||
    assets.isFetching ||
    forecast.isFetching ||
    cashflowEvents.isFetching ||
    riskProfile.isFetching ||
    budget.isFetching

  const loadingIndicators = {
    assets: assets.isAnyLoading,
    cashflowEvents: cashflowEvents.isAnyLoading,
    budget: budget.isAnyLoading,
    liabilities: liabilities.isAnyLoading,
    riskProfile: riskProfile.isAnyLoading,
    notes: notes.isAnyLoading,
    fna: fna.isAnyLoading,
    scenario: scenario.isAnyLoading,
    riskProducts: riskProducts.isAnyLoading
  }

  return {
    scenario,
    assets,
    forecast,
    cashflowEvents,
    liabilities,
    notes,
    riskProfile,
    budget,
    fna,
    riskProducts,
    assetAllocation,

    isFetching,

    loadingIndicators
  }
}
